import React, { useEffect, useState, useCallback } from 'react';
import accessLocalStorage from '../../services/accessLocalStorage';
import './config-styles.css';

const getApiBase = async () => {
  const { ipRequest } = await accessLocalStorage.getUserLocalStorage();
  const ipClean = ipRequest.split('/')[2];
  const host = ipClean.split(':')[0];
  const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
  return `${protocol}//${host}`;
};

const getTenantId = async () => {
  const { user: { idtenant } } = await accessLocalStorage.getUserLocalStorage();
  return String(idtenant);
};

const getToken = async () => {
  const { token } = await accessLocalStorage.getUserLocalStorage();
  return token;
};

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  if (mins === 0) return `${secs}s`;
  return `${mins}m ${secs}s`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR');
};

function Reports() {
  const [loading, setLoading] = useState(false);
  const [calls, setCalls] = useState([]);
  const [metrics, setMetrics] = useState({
    queueMetrics: [],
    agentMetrics: [],
    kpis: {},
  });
  const [activeTab, setActiveTab] = useState('calls');
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    queue: '',
    status: '',
  });

  const fetchCalls = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();
      const token = await getToken();

      const params = new URLSearchParams({
        start_date: filters.startDate,
        end_date: filters.endDate,
      });
      if (filters.queue) params.append('queue', filters.queue);
      if (filters.status) params.append('disposition', filters.status);

      const response = await fetch(`${apiBase}/v2/analytics/history/${tenantId}/calls?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCalls(data.calls || data.data || data || []);
      }
    } catch (e) {
      console.error('Erro ao buscar chamadas:', e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const apiBase = await getApiBase();
      const tenantId = await getTenantId();

      const [queuesRes, agentsRes, kpisRes] = await Promise.all([
        fetch(`${apiBase}/v2/analytics/metrics/${tenantId}/queues`),
        fetch(`${apiBase}/v2/analytics/metrics/${tenantId}/agents`),
        fetch(`${apiBase}/v2/analytics/metrics/${tenantId}/kpis`),
      ]);

      const queueData = queuesRes.ok ? await queuesRes.json() : { queues: [] };
      const agentData = agentsRes.ok ? await agentsRes.json() : { agents: [] };
      const kpiData = kpisRes.ok ? await kpisRes.json() : {};

      setMetrics({
        queueMetrics: queueData.queues || [],
        agentMetrics: agentData.agents || [],
        kpis: kpiData,
      });
    } catch (e) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'calls') {
      fetchCalls();
    } else {
      fetchMetrics();
    }
  }, [activeTab, fetchCalls, fetchMetrics]);

  const getStatusBadge = (status) => {
    const config = {
      answered: { className: 'ccv2-badge--success', label: 'Atendida' },
      abandoned: { className: 'ccv2-badge--danger', label: 'Abandonada' },
      queued: { className: 'ccv2-badge--warn', label: 'Em espera' },
      connected: { className: 'ccv2-badge--info', label: 'Em atendimento' },
    };
    const c = config[status] || { className: '', label: status };
    return (
      <span className={`ccv2-badge ${c.className}`}>
        {c.label}
      </span>
    );
  };

  return (
    <div className="ccv2-config-container">
      <div className="ccv2-config-header">
        <div className="ccv2-config-title-area">
          <h1>Relatórios</h1>
          <p>Histórico de chamadas e métricas do Call Center</p>
        </div>
      </div>

      <div className="ccv2-tabs">
        <button
          className={`ccv2-tab ${activeTab === 'calls' ? 'active' : ''}`}
          onClick={() => setActiveTab('calls')}
        >
          Chamadas
        </button>
        <button
          className={`ccv2-tab ${activeTab === 'queues' ? 'active' : ''}`}
          onClick={() => setActiveTab('queues')}
        >
          Métricas por Fila
        </button>
        <button
          className={`ccv2-tab ${activeTab === 'agents' ? 'active' : ''}`}
          onClick={() => setActiveTab('agents')}
        >
          Métricas por Agente
        </button>
      </div>

      {activeTab === 'calls' && (
        <>
          <div className="ccv2-filters">
            <div className="ccv2-filter-group">
              <label>Data Início</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>
            <div className="ccv2-filter-group">
              <label>Data Fim</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
            <div className="ccv2-filter-group">
              <label>Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Todos</option>
                <option value="answered">Atendidas</option>
                <option value="abandoned">Abandonadas</option>
              </select>
            </div>
            <button className="ccv2-btn ccv2-btn-primary" onClick={fetchCalls}>
              Buscar
            </button>
          </div>

          <div className="ccv2-config-card">
            {loading ? (
              <div className="ccv2-config-loading">
                <div className="ccv2-spinner"></div>
              </div>
            ) : (
              <div className="ccv2-config-table-wrapper">
                <table className="ccv2-config-table">
                  <thead>
                    <tr>
                      <th>Data/Hora</th>
                      <th>Origem</th>
                      <th>Fila</th>
                      <th>Agente</th>
                      <th>Espera</th>
                      <th>Duração</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="ccv2-empty-row">
                          Nenhuma chamada encontrada
                        </td>
                      </tr>
                    ) : (
                      calls.map((call, idx) => (
                        <tr key={call.uniqueId || idx}>
                          <td>{formatDate(call.createdAt || call.startTime)}</td>
                          <td className="ccv2-cell-phone">{call.callerNumber || call.src || '-'}</td>
                          <td>{call.queue || '-'}</td>
                          <td>{call.agentName || call.agent || '-'}</td>
                          <td>{formatDuration(call.waitTime || call.holdtime)}</td>
                          <td>{formatDuration(call.talkTime || call.duration)}</td>
                          <td>{getStatusBadge(call.status || call.disposition)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'queues' && (
        <div className="ccv2-config-card">
          {loading ? (
            <div className="ccv2-config-loading">
              <div className="ccv2-spinner"></div>
            </div>
          ) : (
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Fila</th>
                    <th>Oferecidas</th>
                    <th>Atendidas</th>
                    <th>Abandonadas</th>
                    <th>ASA</th>
                    <th>AHT</th>
                    <th>SLA</th>
                    <th>Abandono</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.queueMetrics.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="ccv2-empty-row">
                        Nenhuma métrica disponível
                      </td>
                    </tr>
                  ) : (
                    metrics.queueMetrics.map((q, idx) => (
                      <tr key={q.queue_key || idx}>
                        <td className="ccv2-cell-name">{q.queue_name || q.queue_key}</td>
                        <td>{q.calls_offered || 0}</td>
                        <td className="ccv2-cell-success">{q.calls_answered || 0}</td>
                        <td className="ccv2-cell-danger">{q.calls_abandoned || 0}</td>
                        <td>{formatDuration(q.asa || 0)}</td>
                        <td>{formatDuration(q.aht || 0)}</td>
                        <td>
                          <span className={`ccv2-metric ${q.service_level >= 80 ? 'good' : q.service_level >= 60 ? 'warn' : 'bad'}`}>
                            {(q.service_level || 0).toFixed(1)}%
                          </span>
                        </td>
                        <td>
                          <span className={`ccv2-metric ${q.abandon_rate <= 5 ? 'good' : q.abandon_rate <= 10 ? 'warn' : 'bad'}`}>
                            {(q.abandon_rate || 0).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'agents' && (
        <div className="ccv2-config-card">
          {loading ? (
            <div className="ccv2-config-loading">
              <div className="ccv2-spinner"></div>
            </div>
          ) : (
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead>
                  <tr>
                    <th>Agente</th>
                    <th>Chamadas</th>
                    <th>Tempo Falado</th>
                    <th>AHT</th>
                    <th>Ocupação</th>
                    <th>Tempo Pausa</th>
                    <th>Disponível</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.agentMetrics.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="ccv2-empty-row">
                        Nenhuma métrica disponível
                      </td>
                    </tr>
                  ) : (
                    metrics.agentMetrics.map((a, idx) => (
                      <tr key={a.agent_key || idx}>
                        <td className="ccv2-cell-name">{a.agent_name || a.agent_key}</td>
                        <td>{a.calls_handled || 0}</td>
                        <td>{formatDuration(a.total_talk_time || 0)}</td>
                        <td>{formatDuration(a.aht || 0)}</td>
                        <td>
                          <span className={`ccv2-metric ${a.occupancy >= 70 ? 'good' : a.occupancy >= 50 ? 'warn' : ''}`}>
                            {(a.occupancy || 0).toFixed(1)}%
                          </span>
                        </td>
                        <td>{formatDuration(a.total_pause_time || 0)}</td>
                        <td>{formatDuration(a.total_available_time || 0)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Reports;
